import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { CurrencyDollar, ChartLineUp, Briefcase, TrendUp, ArrowsClockwise } from '@phosphor-icons/react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const DONUT_COLORS = ['#DC120F', '#0ea5e9', '#22c55e', '#f59e0b'];
const CHART_TICK = { fill: '#94a3b8', fontSize: 12 };
const TT_STYLE = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#0f172a' };

const GMV_TREND = [
  { month: 'Nov', gmv: 620000 },
  { month: 'Dec', gmv: 780000 },
  { month: 'Jan', gmv: 890000 },
  { month: 'Feb', gmv: 1020000 },
  { month: 'Mar', gmv: 1140000 },
  { month: 'Apr', gmv: 1240000 },
];

const CAMPAIGN_SHARE = [
  { name: 'Tech Campaign', value: 38 },
  { name: 'Fashion Sale', value: 28 },
  { name: 'Home & Living', value: 22 },
  { name: 'AEO Campaign', value: 12 },
];

const CLICKS_CONVERSIONS = [
  { month: 'Nov', clicks: 32000, conversions: 980 },
  { month: 'Dec', clicks: 38000, conversions: 1240 },
  { month: 'Jan', clicks: 44000, conversions: 1480 },
  { month: 'Feb', clicks: 48000, conversions: 1620 },
  { month: 'Mar', clicks: 52000, conversions: 1840 },
  { month: 'Apr', clicks: 58000, conversions: 1960 },
];

const KPI = [
  { title: 'Total Spend', value: '₹3,20,000', trend: null, icon: CurrencyDollar, color: '#DC120F' },
  { title: 'GMV Generated', value: '₹12,40,000', trend: null, icon: ChartLineUp, color: '#22c55e' },
  { title: 'ROI', value: '3.87x', trend: '+0.4x', icon: ArrowsClockwise, color: '#0ea5e9' },
  { title: 'Active Campaigns', value: '4', trend: null, icon: Briefcase, color: '#f59e0b' },
];

export default function BrandDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/brand/campaigns`);
        setCampaigns(res.data);
      } catch {
        toast.error('Failed to load campaigns');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout role="brand">
        <div className="p-8">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="brand">
      <div className="p-8" data-testid="brand-dashboard">
        <h1 className="font-heading text-4xl font-bold mb-8">Dashboard</h1>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {KPI.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.title} className="border border-foreground p-5 hover:shadow-lg transition-shadow" data-testid={`kpi-${k.title.toLowerCase().replace(/ /g,'-')}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="rounded-lg p-2" style={{ background: `${k.color}15` }}>
                    <Icon size={20} style={{ color: k.color }} />
                  </div>
                  {k.trend && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <TrendUp size={12} /> {k.trend}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{k.title}</p>
                <p className="mt-1 text-2xl font-bold">{k.value}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid gap-6 xl:grid-cols-3 mb-8">
          {/* GMV Trend */}
          <div className="border border-foreground p-5 xl:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold">GMV Trend</h2>
              <span className="text-xs text-muted-foreground">Monthly GMV</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={GMV_TREND}>
                  <defs>
                    <linearGradient id="gmvFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={CHART_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v/100000).toFixed(1)}L`} />
                  <Tooltip contentStyle={TT_STYLE} formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'GMV']} />
                  <Area type="monotone" dataKey="gmv" stroke="#22c55e" strokeWidth={3} fill="url(#gmvFill)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Campaign Share */}
          <div className="border border-foreground p-5">
            <h2 className="text-lg font-bold mb-5">Campaign Share</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={CAMPAIGN_SHARE} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={50} outerRadius={78} paddingAngle={3}>
                    {CAMPAIGN_SHARE.map((entry, i) => (
                      <Cell key={entry.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TT_STYLE} formatter={(v) => [`${v}%`, 'GMV Contribution']} />
                  <Legend verticalAlign="bottom" height={36} formatter={(v) => <span style={{ color: '#0f172a', fontSize: 11 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Clicks vs Conversions */}
        <div className="border border-foreground p-5 mb-8">
          <h2 className="text-lg font-bold mb-5">Clicks vs Conversions</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={CLICKS_CONVERSIONS}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={CHART_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={CHART_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TT_STYLE} />
                <Bar dataKey="clicks" name="Clicks" fill="#DC120F" radius={[6, 6, 0, 0]} />
                <Bar dataKey="conversions" name="Conversions" fill="#d4d4d4" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Campaigns Table */}
        <div className="border border-foreground" data-testid="recent-campaigns-section">
          <div className="border-b border-foreground p-6">
            <h2 className="text-lg font-bold">Recent Campaigns</h2>
          </div>
          <div className="p-6">
            {campaigns.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No campaigns yet. Submit a brief to get started.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-foreground text-left text-xs uppercase text-muted-foreground">
                      <th className="pb-3">Campaign</th>
                      <th className="pb-3">Category</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3 text-right">Budget</th>
                      <th className="pb-3 text-right">Est. ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.slice(0, 5).map((c) => (
                      <tr key={c.id} className="border-b border-border">
                        <td className="py-3 font-medium">{c.name}</td>
                        <td className="py-3 text-muted-foreground">{c.category}</td>
                        <td className="py-3">
                          <span className={`px-2.5 py-1 text-xs font-medium border ${c.status === 'active' ? 'bg-accent border-foreground' : 'border-foreground'}`}>
                            {c.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 text-right font-mono">₹{(c.content_budget + c.distribution_budget).toLocaleString('en-IN')}</td>
                        <td className="py-3 text-right font-mono">{c.estimated_outcomes?.roi ? `${(c.estimated_outcomes.roi * 100).toFixed(1)}%` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
